import { useSelector } from "react-redux";
import React, { useState, useEffect, useCallback } from "react";
import AdminShell from "./AdminShell";
import { dialog } from "../Common/TreasuryDialog";

const API = process.env.REACT_APP_API_URL || "";

const FeeSettings = () => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  // Track which fee rows are currently being edited (unlocked)
  const [editingKeys, setEditingKeys] = useState({});
  // Track per-row saving state
  const [savingKey, setSavingKey] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("fees");

  // Generate invoice form
  const [genForm, setGenForm] = useState({ type: "listing", userId: "", propertyId: "", subtotal: "", notes: "" });
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [generating, setGenerating] = useState(false);

  const { auth } = useSelector((state) => state);
  const token = auth?.data?.accesstoken || auth?.data?.access_token;
  const authHeader = { Authorization: `Bearer ${token}` };

  const loadSettings = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/admin/fee-settings`, { headers: authHeader })
      .then((r) => r.json())
      .then((data) => {
        setSettings(data.settings);
        setForm({
          listingFee: data.settings?.listingFee ?? 2,
          subscriptionFee: data.settings?.subscriptionFee ?? 0,
          managementFee: data.settings?.managementFee ?? 1.5,
          exchangeFeePerParty: data.settings?.exchangeFeePerParty ?? 0.25,
          vatRate: data.settings?.vatRate ?? 15,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  const loadInvoices = useCallback(() => {
    const url = invoiceFilter
      ? `${API}/api/admin/invoices?type=${invoiceFilter}`
      : `${API}/api/admin/invoices`;
    fetch(url, { headers: authHeader })
      .then((r) => r.json())
      .then((data) => setInvoices(data.invoices || []))
      .catch(console.error);
  }, [token, invoiceFilter]);

  useEffect(() => {
    if (!token) return;
    loadSettings();
    fetch(`${API}/api/get_property`, { headers: authHeader })
      .then((r) => r.json())
      .then((d) => setProperties(Array.isArray(d) ? d : (d.data || d.properties || [])))
      .catch(console.error);
    fetch(`${API}/api/get_user`, { headers: authHeader })
      .then((r) => r.json())
      .then((d) => setUsers(Array.isArray(d) ? d : (d.data || d.users || [])))
      .catch(console.error);
  }, [token]);

  useEffect(() => {
    if (activeTab === "invoices") loadInvoices();
  }, [activeTab, loadInvoices]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/fee-settings`, {
        method: "PUT",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.status === 1) {
        dialog.success("Saved", "Fee settings updated successfully.");
        loadSettings();
      } else {
        dialog.error("Error", data.msg);
      }
    } catch {
      dialog.error("Error", "Network error.");
    } finally {
      setSaving(false);
    }
  };

  // Save a single fee row and lock it again
  const handleSaveRow = async (key) => {
    setSavingKey(key);
    try {
      const res = await fetch(`${API}/api/admin/fee-settings`, {
        method: "PUT",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.status === 1) {
        setEditingKeys((prev) => ({ ...prev, [key]: false }));
        loadSettings();
      } else {
        dialog.error("Error", data.msg);
      }
    } catch {
      dialog.error("Error", "Network error.");
    } finally {
      setSavingKey(null);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!genForm.subtotal || parseFloat(genForm.subtotal) <= 0) {
      dialog.error("Validation", "Subtotal amount is required.");
      return;
    }
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/admin/invoices/generate`, {
        method: "POST",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ ...genForm, subtotal: parseFloat(genForm.subtotal) }),
      });
      const data = await res.json();
      if (data.status === 1) {
        dialog.success("Invoice Generated", `Invoice ${data.invoice.invoiceNumber} created successfully.`);
        setGenForm({ type: "listing", userId: "", propertyId: "", subtotal: "", notes: "" });
        loadInvoices();
        setActiveTab("invoices");
      } else {
        dialog.error("Error", data.msg);
      }
    } catch {
      dialog.error("Error", "Network error.");
    } finally {
      setGenerating(false);
    }
  };

  const vatPreview = form.subtotal
    ? (parseFloat(form.subtotal || 0) * (form.vatRate / 100)).toFixed(2)
    : null;

  const feeItems = [
    { key: "listingFee", label: "Listing Fee", desc: "Charged when a property is listed for tokenization" },
    { key: "subscriptionFee", label: "Subscription Fee", desc: "Charged on primary market token subscriptions" },
    { key: "managementFee", label: "Management Fee", desc: "Annual management fee on property value" },
    { key: "exchangeFeePerParty", label: "Exchange Fee (per party)", desc: "Charged to each party on secondary market trades" },
    { key: "vatRate", label: "VAT Rate", desc: "Saudi VAT (ZATCA) — currently 15%" },
  ];

  const typeColors = {
    listing: { bg: "#dbeafe", color: "#1e40af" },
    subscription: { bg: "#dcfce7", color: "#166534" },
    management: { bg: "#fef9c3", color: "#854d0e" },
    exchange: { bg: "#f3e8ff", color: "#6b21a8" },
  };

  return (
    <AdminShell pageTitle="Fees & ZATCA Invoicing">
      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 28, borderBottom: "2px solid #e5e7eb" }}>
        {["fees", "generate", "invoices"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              background: "none",
              border: "none",
              padding: "10px 24px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              color: activeTab === tab ? "#c9a84c" : "#6b7280",
              borderBottom: activeTab === tab ? "2px solid #c9a84c" : "2px solid transparent",
              marginBottom: -2,
              transition: "all 0.18s",
              textTransform: "capitalize",
            }}
          >
            {tab === "fees" ? "Fee Structure" : tab === "generate" ? "Generate Invoice" : "Invoice History"}
          </button>
        ))}
      </div>

      {/* Fee Structure */}
      {activeTab === "fees" && (
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 24 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, boxShadow: "0 1px 8px rgba(0,0,0,0.07)" }}>
            <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#111827" }}>Fee Structure</h3>
            <p style={{ margin: "0 0 24px", fontSize: 13, color: "#6b7280" }}>
              Configure platform fees and Saudi VAT rate (ZATCA compliant)
            </p>
            {loading ? (
              <div style={{ textAlign: "center", color: "#9ca3af", padding: "30px 0" }}>Loading…</div>
            ) : (
              <>
                {feeItems.map((item) => {
                  const isEditing = !!editingKeys[item.key];
                  const isSavingThis = savingKey === item.key;
                  return (
                    <div
                      key={item.key}
                      style={{
                        marginBottom: 12,
                        padding: "14px 16px",
                        borderRadius: 10,
                        border: isEditing ? "1.5px solid #c9a84c" : "1.5px solid #e5e7eb",
                        background: isEditing ? "#fffdf5" : "#fafafa",
                        transition: "all 0.18s",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 2 }}>{item.label}</div>
                          <div style={{ fontSize: 11, color: "#9ca3af" }}>{item.desc}</div>
                        </div>
                        {/* Locked view */}
                        {!isEditing && (
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <span style={{ fontSize: 20, fontWeight: 800, color: "#c9a84c", minWidth: 52, textAlign: "right" }}>
                              {form[item.key] ?? "—"}%
                            </span>
                            <button
                              onClick={() => setEditingKeys((prev) => ({ ...prev, [item.key]: true }))}
                              style={{
                                display: "flex", alignItems: "center", gap: 5,
                                padding: "6px 14px", borderRadius: 7,
                                border: "1.5px solid #e5e7eb",
                                background: "#fff",
                                fontSize: 12, fontWeight: 600, color: "#374151",
                                cursor: "pointer",
                              }}
                            >
                              ✏️ Edit
                            </button>
                          </div>
                        )}
                        {/* Editing view */}
                        {isEditing && (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              autoFocus
                              value={form[item.key] ?? ""}
                              onChange={(e) => setForm((f) => ({ ...f, [item.key]: parseFloat(e.target.value) || 0 }))}
                              style={{
                                width: 90,
                                padding: "7px 10px",
                                border: "1.5px solid #c9a84c",
                                borderRadius: 7,
                                fontSize: 15,
                                fontWeight: 700,
                                color: "#111827",
                                outline: "none",
                                background: "#fff",
                              }}
                            />
                            <span style={{ fontSize: 14, color: "#6b7280", fontWeight: 600 }}>%</span>
                            <button
                              onClick={() => handleSaveRow(item.key)}
                              disabled={isSavingThis}
                              style={{
                                padding: "7px 16px", borderRadius: 7,
                                background: isSavingThis ? "#e5e7eb" : "linear-gradient(135deg,#c9a84c,#7a5c10)",
                                color: isSavingThis ? "#9ca3af" : "#fff",
                                border: "none", fontSize: 12, fontWeight: 700,
                                cursor: isSavingThis ? "not-allowed" : "pointer",
                              }}
                            >
                              {isSavingThis ? "Saving…" : "Save"}
                            </button>
                            <button
                              onClick={() => {
                                // Revert to saved value and lock
                                setForm((f) => ({ ...f, [item.key]: settings?.[item.key] ?? f[item.key] }));
                                setEditingKeys((prev) => ({ ...prev, [item.key]: false }));
                              }}
                              style={{
                                padding: "7px 12px", borderRadius: 7,
                                border: "1.5px solid #e5e7eb",
                                background: "#fff",
                                fontSize: 12, fontWeight: 600, color: "#6b7280",
                                cursor: "pointer",
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Summary cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {feeItems.slice(0, 4).map((item) => (
              <div
                key={item.key}
                style={{
                  background: "#fff",
                  borderRadius: 10,
                  padding: "16px 20px",
                  boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 2 }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: "#d1d5db" }}>{item.desc}</div>
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, color: "#c9a84c" }}>
                  {form[item.key] ?? "—"}%
                </div>
              </div>
            ))}
            <div
              style={{
                background: "#0e3725",
                borderRadius: 10,
                padding: "16px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginBottom: 2 }}>Saudi VAT (ZATCA)</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>Applied to all invoices</div>
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#c9a84c" }}>{form.vatRate ?? 15}%</div>
            </div>
          </div>
        </div>
      )}

      {/* Generate Invoice */}
      {activeTab === "generate" && (
        <div style={{ background: "#fff", borderRadius: 12, padding: 28, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", maxWidth: 600 }}>
          <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#111827" }}>Generate ZATCA Invoice</h3>
          <p style={{ margin: "0 0 24px", fontSize: 13, color: "#6b7280" }}>
            Creates a ZATCA Phase 1 compliant e-invoice with TLV QR code
          </p>
          <form onSubmit={handleGenerate}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>Invoice Type *</label>
                <select
                  value={genForm.type}
                  onChange={(e) => setGenForm((f) => ({ ...f, type: e.target.value }))}
                  style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13 }}
                >
                  <option value="listing">Listing Fee</option>
                  <option value="subscription">Subscription Fee</option>
                  <option value="management">Management Fee</option>
                  <option value="exchange">Exchange Fee</option>
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>Subtotal (SAR, excl. VAT) *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={genForm.subtotal}
                  onChange={(e) => setGenForm((f) => ({ ...f, subtotal: e.target.value }))}
                  placeholder="e.g. 5000"
                  style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }}
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>Investor (optional)</label>
                <select
                  value={genForm.userId}
                  onChange={(e) => setGenForm((f) => ({ ...f, userId: e.target.value }))}
                  style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13 }}
                >
                  <option value="">— Select investor —</option>
                  {users.map((u) => (
                    <option key={u._id} value={u._id}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>Property (optional)</label>
                <select
                  value={genForm.propertyId}
                  onChange={(e) => setGenForm((f) => ({ ...f, propertyId: e.target.value }))}
                  style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13 }}
                >
                  <option value="">— Select property —</option>
                  {properties.map((p) => (
                    <option key={p._id} value={p._id}>{p.title}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>Notes (optional)</label>
              <input
                value={genForm.notes}
                onChange={(e) => setGenForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Invoice notes"
                style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }}
              />
            </div>

            {/* VAT preview */}
            {genForm.subtotal && (
              <div style={{ background: "#fefce8", border: "1px solid #fde68a", borderRadius: 8, padding: "12px 16px", marginBottom: 20, fontSize: 13 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, color: "#78350f" }}>
                  <span>Subtotal:</span>
                  <strong>SAR {parseFloat(genForm.subtotal).toLocaleString()}</strong>
                  <span>VAT ({settings?.vatRate || 15}%):</span>
                  <strong>SAR {(parseFloat(genForm.subtotal) * ((settings?.vatRate || 15) / 100)).toFixed(2)}</strong>
                  <span style={{ borderTop: "1px solid #fde68a", paddingTop: 4 }}>Total:</span>
                  <strong style={{ borderTop: "1px solid #fde68a", paddingTop: 4 }}>
                    SAR {(parseFloat(genForm.subtotal) * (1 + (settings?.vatRate || 15) / 100)).toFixed(2)}
                  </strong>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={generating}
              style={{
                padding: "11px 28px",
                background: generating ? "#e5e7eb" : "linear-gradient(135deg,#c9a84c,#7a5c10)",
                color: generating ? "#9ca3af" : "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 700,
                cursor: generating ? "not-allowed" : "pointer",
              }}
            >
              {generating ? "Generating…" : "Generate Invoice"}
            </button>
          </form>
        </div>
      )}

      {/* Invoice History */}
      {activeTab === "invoices" && (
        <div style={{ background: "#fff", borderRadius: 12, padding: 28, boxShadow: "0 1px 8px rgba(0,0,0,0.07)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>Invoice History</h3>
            <select
              value={invoiceFilter}
              onChange={(e) => setInvoiceFilter(e.target.value)}
              style={{ padding: "7px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13 }}
            >
              <option value="">All Types</option>
              <option value="listing">Listing</option>
              <option value="subscription">Subscription</option>
              <option value="management">Management</option>
              <option value="exchange">Exchange</option>
            </select>
          </div>
          {invoices.length === 0 ? (
            <div style={{ textAlign: "center", color: "#9ca3af", padding: "40px 0", fontSize: 14 }}>
              No invoices found
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #f3f4f6" }}>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Invoice #</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Date</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Type</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Buyer</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Subtotal</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>VAT</th>
                  <th style={{ textAlign: "right", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Total</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const tc = typeColors[inv.type] || { bg: "#f3f4f6", color: "#374151" };
                  return (
                    <tr key={inv._id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "12px 12px", fontFamily: "monospace", fontSize: 12, color: "#374151" }}>
                        {inv.invoiceNumber}
                      </td>
                      <td style={{ padding: "12px 12px", color: "#374151" }}>
                        {new Date(inv.issueDate).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}
                      </td>
                      <td style={{ padding: "12px 12px" }}>
                        <span style={{ background: tc.bg, color: tc.color, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: "capitalize" }}>
                          {inv.type}
                        </span>
                      </td>
                      <td style={{ padding: "12px 12px", color: "#111827" }}>
                        {inv.buyerName || inv.userId?.name || "—"}
                      </td>
                      <td style={{ padding: "12px 12px", textAlign: "right", color: "#374151" }}>
                        {inv.subtotal.toLocaleString()}
                      </td>
                      <td style={{ padding: "12px 12px", textAlign: "right", color: "#6b7280" }}>
                        {inv.vatAmount.toFixed(2)}
                      </td>
                      <td style={{ padding: "12px 12px", textAlign: "right", fontWeight: 700, color: "#c9a84c" }}>
                        {inv.total.toLocaleString()}
                      </td>
                      <td style={{ padding: "12px 12px" }}>
                        <span style={{
                          background: inv.zatcaStatus === "cleared" ? "#dcfce7" : "#fef9c3",
                          color: inv.zatcaStatus === "cleared" ? "#166534" : "#854d0e",
                          padding: "2px 10px",
                          borderRadius: 20,
                          fontSize: 11,
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}>
                          {inv.zatcaStatus}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </AdminShell>
  );
};

export default FeeSettings;
